import * as arrow from "apache-arrow";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useCustomer } from "./useCustomer";
import { viewTransformsClient } from "../services/viewtransforms/apollo-client";
import {
  CREATE_MY_VIEW_TRANSFORM_VT,
  type CreateMyViewTransformVTInput,
  type CreateMyViewTransformVTResult,
} from "../services/viewtransforms/mutations/create_my_view_transform";
import { useViewTransformSubscription } from "../services/appsync/hooks";
import { appSyncService } from "../services/appsync/service";
import { parseParquetFromBase64, parseParquetFromUrl } from "../utils/parquet";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface UseViewTransformOptions {
  transformId: string;
  /** Optional — backend resolves to latest successful run if omitted. */
  runSettings?: string;
  initialParams?: unknown;
  organizationId?: string;
  /**
   * Optional opaque post-process descriptor forwarded to the parquet worker so
   * derived rows are materialized in the same parse pass (no second Arrow IPC
   * round-trip). The worker interprets it; the result is exposed verbatim as
   * `postProcessResult`. Used by RetailerSupplyView for SkuRow[] (Phase 7 Step 2).
   */
  postProcess?: unknown;
}

interface UseViewTransformReturn {
  arrowTable: arrow.Table | null;
  columns: string[];
  rowCount: number;
  loading: boolean;
  refreshing: boolean;
  error: Error | null;
  /** Result of the optional in-worker post-process, or null. Caller casts. */
  postProcessResult: unknown;
  createViewTransform: (params: unknown, options?: { forceRecalc?: boolean }) => Promise<void>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useViewTransform<_T>(options: UseViewTransformOptions): UseViewTransformReturn {
  const { transformId, runSettings, initialParams, organizationId, postProcess } = options;
  const { selectedCustomerId } = useCustomer();

  const orgId = organizationId || selectedCustomerId;

  // Stable transformationSettings string derived from initialParams
  const transformationSettings = useMemo(
    () => (initialParams != null ? JSON.stringify(initialParams) : "{}"),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally stringify once per value
    [JSON.stringify(initialParams)]
  );

  // ── State ────────────────────────────────────────────────────────────────
  const [arrowTable, setArrowTable] = useState<arrow.Table | null>(null);
  const [columns, setColumns] = useState<string[]>([]);
  const [postProcessResult, setPostProcessResult] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // When the caller omits runSettings, the backend resolves it and returns
  // `runSettingsUsed`. We store it here so the subscription can use it.
  const [resolvedRunSettings, setResolvedRunSettings] = useState(runSettings ?? "");

  // CHO-617 Phase 2: the AppSync subscription is a fallback for VTs that don't
  // complete inside the mutation's inline poll window. It's opened lazily —
  // only after a mutation returns IN_PROGRESS — so the common fast path never
  // opens a socket. Set false again once the result arrives (or a new mutation
  // fires), which tears the socket down.
  const [awaitingSubscription, setAwaitingSubscription] = useState(false);

  // Keep resolvedRunSettings in sync if the caller provides/changes runSettings
  useEffect(() => {
    if (runSettings) setResolvedRunSettings(runSettings);
  }, [runSettings]);

  // Track which VT ID we're waiting on to ignore stale subscription updates
  const pendingVtIdRef = useRef<string | null>(null);
  // Bind a subscription completion to the request that opened it. A VT id
  // alone is not enough because a newer stage selection may start while the
  // prior result is still parsing.
  const pendingRequestIdRef = useRef<number | null>(null);
  // Track the latest requested params to ignore stale responses
  const latestRequestRef = useRef(0);
  // Track when the current cold-load began so each phase can log elapsed time
  const perfStartRef = useRef<number | null>(null);
  // Guard against setting state after unmount
  const mountedRef = useRef(true);
  // Track the (runSettings, transformationSettings) the currently-loaded result
  // was computed against. A late-resolving runSettings that matches what we
  // already computed for the same params (the common case: the null-fire
  // resolved to the latest run, which equals the now-resolved selected run)
  // must NOT trigger a wasteful re-fire + Arrow re-parse + derived-state
  // recompute. See CHO-617 Phase 1.
  const lastComputedRunSettingsRef = useRef("");
  const lastComputedTransformationSettingsRef = useRef("");

  // CHO-617 Phase 3 (Target A): kill the double fanout. On a cold open the
  // optimistic null-fire and the runs-query-resolved fire converge to the same
  // canonical runSettings; we must not blindly re-fire just because the runs
  // query won the race against the first mutation. These refs let the
  // auto-trigger defer to an in-flight optimistic fire, and let the fire's
  // completion handler decide (at mutation-return time, when runSettingsUsed is
  // known) whether a genuine non-latest selection still needs a follow-up.
  const inFlightRef = useRef(false);
  const firedWithRunSettingsRef = useRef("");
  const firedWithTransformationSettingsRef = useRef("");
  // Latest values read by async completion handlers (which run long after the
  // fire that created their closure).
  const runSettingsRef = useRef(runSettings);
  const initialParamsRef = useRef(initialParams);

  // Log elapsed-time phases relative to the most recent callMutation
  const perfLog = useCallback(
    (phase: string, extra?: Record<string, unknown>) => {
      const now = performance.now();
      const start = perfStartRef.current ?? now;
      const elapsedMs = (now - start).toFixed(0);
      console.log(`[PERF] [FE] [${transformId}] ${phase}`, { elapsedMs, ...extra });
    },
    [transformId]
  );
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Keep refs read by async completion handlers pointed at the latest values.
  useEffect(() => {
    runSettingsRef.current = runSettings;
    initialParamsRef.current = initialParams;
  });

  // ── Subscription ───────────────────────────────────────────────────────────
  // Opened only when a mutation returned IN_PROGRESS (awaitingSubscription) AND
  // we have runSettings for the filtered subscription. On the fast path the
  // mutation returns COMPLETED synchronously, awaitingSubscription stays false,
  // and no socket is ever opened.
  const subscriptionEnabled = awaitingSubscription && !!resolvedRunSettings;
  const { viewTransforms } = useViewTransformSubscription(
    {
      organizationId: orgId,
      transformId,
      runSettings: resolvedRunSettings,
      transformationSettings,
    },
    { enabled: subscriptionEnabled }
  );

  // ── Parse parquet helper ─────────────────────────────────────────────────
  const parseAndSet = useCallback(
    async (
      requestId: number,
      s3Url: string | null | undefined,
      parquetData: string | null | undefined
    ): Promise<boolean> => {
      try {
        let result: { arrowTable: arrow.Table; columns: string[]; postProcessResult?: unknown };
        const transport = parquetData ? "base64" : "s3";
        perfLog("parse_start", { transport, s3Url: s3Url ?? null });
        const parseStart = performance.now();

        if (parquetData) {
          result = await parseParquetFromBase64(parquetData, postProcess);
        } else if (s3Url) {
          result = await parseParquetFromUrl(s3Url, postProcess);
        } else {
          throw new Error("No parquet data or S3 URL in VT response");
        }

        perfLog("parse_done", {
          transport,
          parseMs: (performance.now() - parseStart).toFixed(0),
          rows: result.arrowTable.numRows,
          columns: result.columns.length,
        });

        if (requestId !== latestRequestRef.current) {
          perfLog("parse_stale_result_ignored", { requestId });
          return false;
        }

        if (mountedRef.current) {
          setArrowTable(result.arrowTable);
          setColumns(result.columns);
          setPostProcessResult(result.postProcessResult ?? null);
          setLoading(false);
          setRefreshing(false);
          setError(null);
          perfLog("arrow_table_set", {
            rows: result.arrowTable.numRows,
            columns: result.columns.length,
          });
        }
        return true;
      } catch (err) {
        const stale = requestId !== latestRequestRef.current;
        if (mountedRef.current && !stale) {
          setError(err instanceof Error ? err : new Error(String(err)));
          setLoading(false);
          setRefreshing(false);
        }
        perfLog(stale ? "parse_stale_error_ignored" : "parse_error", {
          error: err instanceof Error ? err.message : String(err),
        });
        return false;
      }
    },
    [perfLog, postProcess]
  );

  // Points at the latest callMutation so completion handlers re-fire with the
  // most recently resolved runSettings (not the empty value their own closure
  // was created with).
  const callMutationRef = useRef<
    ((params: unknown, options?: { forceRecalc?: boolean }) => Promise<void>) | null
  >(null);

  // CHO-617 Phase 3 (Target A): reconcile at mutation-return time. The fire that
  // just completed computed against lastComputedRunSettingsRef; if the runs
  // query has since resolved runSettings to a genuinely different (non-latest)
  // value, re-fire once with that explicit value. When they're equal — the
  // common cold-load case where the null-fire resolved to the latest run that
  // the selector also picked — we do nothing, eliminating the wasted second
  // fanout.
  const maybeRefireForRunSettings = useCallback(() => {
    const resolved = runSettingsRef.current || "";
    if (resolved !== "" && resolved !== lastComputedRunSettingsRef.current) {
      perfLog("runsettings_reconcile_refire", {
        computed: lastComputedRunSettingsRef.current,
        resolved,
      });
      callMutationRef.current?.(initialParamsRef.current);
    }
  }, [perfLog]);

  // ── React to subscription updates ────────────────────────────────────────
  useEffect(() => {
    if (!subscriptionEnabled || viewTransforms.length === 0) return;

    // Find the VT we're waiting on
    const match = viewTransforms.find((vt) => vt.id === pendingVtIdRef.current);
    if (!match) return;
    const requestId = pendingRequestIdRef.current;
    if (requestId === null || requestId !== latestRequestRef.current) return;

    if (match.status === "COMPLETED") {
      perfLog("subscription_completed", { vtId: match.id });
      pendingVtIdRef.current = null;
      pendingRequestIdRef.current = null;
      if (mountedRef.current) setAwaitingSubscription(false);
      void parseAndSet(requestId, match.s3Url, null).then((applied) => {
        if (requestId !== latestRequestRef.current) return;
        inFlightRef.current = false;
        if (!applied) return;
        lastComputedRunSettingsRef.current = resolvedRunSettings;
        lastComputedTransformationSettingsRef.current = transformationSettings;
        // Reconcile in case runSettings resolved to a non-latest value while
        // the VT was computing out-of-band.
        maybeRefireForRunSettings();
      });
    } else if (match.status === "FAILED") {
      perfLog("subscription_failed", {
        vtId: match.id,
        errorMessage: match.errorMessage ?? null,
      });
      inFlightRef.current = false;
      if (mountedRef.current) {
        setError(new Error(match.errorMessage || "View transform failed"));
        setLoading(false);
        setRefreshing(false);
        setAwaitingSubscription(false);
      }
      pendingVtIdRef.current = null;
      pendingRequestIdRef.current = null;
    }
    // IN_PROGRESS: keep waiting
  }, [
    viewTransforms,
    subscriptionEnabled,
    parseAndSet,
    perfLog,
    resolvedRunSettings,
    transformationSettings,
    maybeRefireForRunSettings,
  ]);

  // ── Mutation caller ──────────────────────────────────────────────────────
  const callMutation = useCallback(
    async (params: unknown, options?: { forceRecalc?: boolean }) => {
      const requestId = ++latestRequestRef.current;
      const tsString = params != null ? JSON.stringify(params) : "{}";

      // Record what this fire was launched with so the auto-trigger can defer to
      // it (instead of blindly re-firing) while it's in flight. Cleared at every
      // terminal state below.
      inFlightRef.current = true;
      firedWithRunSettingsRef.current = runSettings || "";
      firedWithTransformationSettingsRef.current = tsString;

      // Reset perf clock for this cold-load attempt
      perfStartRef.current = performance.now();
      // Each fire assumes the synchronous/inline path; only an IN_PROGRESS
      // response below re-enables the subscription.
      pendingVtIdRef.current = null;
      pendingRequestIdRef.current = null;
      if (mountedRef.current) setAwaitingSubscription(false);
      perfLog("mutation_start", {
        forceRecalc: !!options?.forceRecalc,
        hasArrowTable: !!arrowTable,
      });

      // Set loading state
      if (mountedRef.current) {
        if (arrowTable) {
          setRefreshing(true); // keep previous data visible
        } else {
          setLoading(true);
        }
        setError(null);
      }

      try {
        const input: CreateMyViewTransformVTInput = {
          transformId,
          transformationSettings: tsString,
          runSettings: runSettings || null,
          ...(options?.forceRecalc ? { forceRecalc: true } : {}),
        };

        const mutateStart = performance.now();
        const { data } = await viewTransformsClient.mutate<CreateMyViewTransformVTResult>({
          mutation: CREATE_MY_VIEW_TRANSFORM_VT,
          variables: { input },
          fetchPolicy: "no-cache",
        });
        const mutateMs = (performance.now() - mutateStart).toFixed(0);

        // Ignore stale responses — a newer request has been fired
        if (requestId !== latestRequestRef.current) {
          perfLog("mutation_stale_response_ignored", { mutateMs });
          return;
        }

        const result = data?.createMyViewTransform;
        if (!result) throw new Error("No result from createMyViewTransform");

        perfLog("mutation_returned", {
          mutateMs,
          status: result.status,
          vtId: result.id,
          hasParquetData: !!result.parquetData,
          hasS3Url: !!result.s3Url,
        });

        // If the VT service resolved runSettings, store it for the subscription fallback
        if (result.runSettingsUsed && mountedRef.current) {
          setResolvedRunSettings(result.runSettingsUsed);
        }

        if (result.status === "COMPLETED") {
          // Record what this result was computed against so the auto-trigger
          // can skip a redundant re-fire when runSettings later resolves to the
          // same value. runSettingsUsed is set only when we sent null (server
          // resolved latest); for an explicit send it's null, so fall back to
          // the runSettings we actually sent.
          const applied = await parseAndSet(requestId, result.s3Url, result.parquetData);
          if (requestId !== latestRequestRef.current) return;
          // This fire is done; reconcile against any runSettings the runs query
          // resolved while it was in flight (re-fires only on a genuine
          // non-latest mismatch — the equal case is a no-op).
          inFlightRef.current = false;
          if (applied) {
            lastComputedRunSettingsRef.current = result.runSettingsUsed || runSettings || "";
            lastComputedTransformationSettingsRef.current = tsString;
            maybeRefireForRunSettings();
          }
        } else if (result.status === "IN_PROGRESS") {
          // The resolver polls ~30s before returning IN_PROGRESS, so this is
          // rare (a concurrent compute of the same VT exceeding the window).
          // Open the AppSync subscription now to receive the eventual result.
          pendingVtIdRef.current = result.id;
          pendingRequestIdRef.current = requestId;
          if (mountedRef.current) setAwaitingSubscription(true);
          perfLog("awaiting_subscription", { vtId: result.id });
          // Safety net: a COMPLETED committed during the brief socket-open
          // window wouldn't be delivered over the subscription. Read once to
          // catch it, ignoring the read if a newer request has superseded us.
          const existing = await appSyncService.getViewTransform(result.id);
          if (
            existing?.status === "COMPLETED" &&
            requestId === latestRequestRef.current &&
            pendingVtIdRef.current === result.id
          ) {
            perfLog("awaiting_subscription_read_hit", { vtId: result.id });
            pendingVtIdRef.current = null;
            pendingRequestIdRef.current = null;
            if (mountedRef.current) setAwaitingSubscription(false);
            const applied = await parseAndSet(requestId, existing.s3Url, null);
            if (requestId !== latestRequestRef.current) return;
            inFlightRef.current = false;
            if (applied) {
              lastComputedRunSettingsRef.current = result.runSettingsUsed || runSettings || "";
              lastComputedTransformationSettingsRef.current = tsString;
              maybeRefireForRunSettings();
            }
          }
        } else if (result.status === "FAILED") {
          throw new Error(result.errorMessage || "View transform failed");
        }
      } catch (err) {
        if (requestId === latestRequestRef.current) inFlightRef.current = false;
        if (mountedRef.current && requestId === latestRequestRef.current) {
          setError(err instanceof Error ? err : new Error(String(err)));
          setLoading(false);
          setRefreshing(false);
        }
        perfLog("mutation_error", {
          error: err instanceof Error ? err.message : String(err),
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      transformId,
      runSettings,
      resolvedRunSettings,
      parseAndSet,
      arrowTable,
      perfLog,
      maybeRefireForRunSettings,
    ]
  );

  // Keep callMutationRef pointed at the latest closure so the reconcile re-fire
  // sends the most recently resolved runSettings.
  useEffect(() => {
    callMutationRef.current = callMutation;
  }, [callMutation]);

  // ── Auto-trigger mutation when params change ──────────────────────────────
  useEffect(() => {
    if (initialParams === undefined) return;
    // Skip the re-fire when a late-resolving runSettings matches what we already
    // computed for these same params. The common cold-load path fires once with
    // runSettings=null (server resolves the latest run), then the runs query
    // resolves the selected run; when they're equal — which they are unless the
    // user picked a non-latest run — re-firing would only repeat the VT round
    // trip and the expensive Arrow parse + derived-state recompute. A genuine
    // mismatch (non-latest run) still re-fires with the explicit runSettings.
    if (
      (runSettings || "") === lastComputedRunSettingsRef.current &&
      transformationSettings === lastComputedTransformationSettingsRef.current
    ) {
      return;
    }
    // Defer to an in-flight optimistic fire. When the first fire was launched
    // with null/empty runSettings (cold-load null-fire) for these same params
    // and is still running, do NOT start a second mutation just because the
    // runs query resolved runSettings first. The in-flight fire's completion
    // handler reconciles runSettingsUsed against the resolved value and
    // re-fires only on a genuine non-latest mismatch. This is the fix for the
    // wasted double fanout (CHO-617 Phase 3, Target A).
    if (
      inFlightRef.current &&
      (firedWithRunSettingsRef.current || "") === "" &&
      transformationSettings === firedWithTransformationSettingsRef.current
    ) {
      return;
    }
    callMutation(initialParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transformId, runSettings, transformationSettings]);

  return {
    arrowTable,
    columns,
    rowCount: arrowTable?.numRows ?? 0,
    loading,
    refreshing,
    error,
    postProcessResult,
    createViewTransform: callMutation,
  };
}
